import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, PageHeader } from '@/components/ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { hiredEquipmentPoService } from '../../../services/hiredEquipmentPoService';
import { usePermissions } from '../../../hooks/usePermissions';
import { HE_AUTO_APPROVE_PERMISSION } from '../../forms/utils/submissionPermissions';
import HeVendorApprovalEmailModal from '../components/HeVendorApprovalEmailModal';
import HeConfirmModal from '../components/HeConfirmModal';
import type { TaskType } from '../../../types/Task';
import HiredEquipmentGrid from '../components/HiredEquipmentGrid';
import HiredEquipmentInternalComment from '../components/HiredEquipmentInternalComment';
import HiredEquipmentTaskModals, {
  type HiredEquipmentTaskModalRequest,
} from '../components/HiredEquipmentTaskModals';
import PoBalanceWarningModal from '../components/PoBalanceWarningModal';
import SubmissionConfirmationModal from '../components/SubmissionConfirmationModal';
import { useHiredEquipmentGridData } from '../hooks/useHiredEquipmentGridData';
import { useHeLinePricing } from '../hooks/useHeLinePricing';
import { usePoBalanceWarning } from '../hooks/usePoBalanceWarning';
import type { HiredEquipmentAnchor, HiredEquipmentGridRow, TaskOption } from '../types';
import { persistHiredEquipmentAnchor, readHiredEquipmentAnchor, clearHiredEquipmentAnchor } from '../utils/anchorStorage';
import { formatAnchorValue, hiredEquipmentGridPageTitle, methodLabel } from '../utils/buildAnchor';
import { defaultFuelSurchargeEligibleFromVehicleType } from '../utils/fuelSurchargeDefault';
import { heGridSubmitRows } from '../utils/heSummaryRows';
import {
  formatIncompleteLinesMessage,
  scrollToGridLine,
  validateGridRows,
} from '../utils/gridValidation';
import {
  appendMissingPoResources,
  duplicateRow,
  emptyRowFromAnchor,
  formatTaskLabel,
  lumpSumRowFromAnchor,
  rowsFromPoResources,
} from '../utils/poPrefill';
import { nextRowAfterTaskChange } from '../utils/poTaskFilter';
import { SUBMISSIONS_DRAFTS_PATH, submissionRecordPath } from '../../forms/submissionsPaths';
import {
  discardHiredEquipmentDraft,
  getHiredEquipmentDraft,
  saveHiredEquipmentDraft,
  submitHiredEquipment,
  type HeVendorEmailDraft,
  type HiredEquipmentSubmitPayload,
} from '../../../services/hiredEquipmentService';

interface GridLocationState {
  anchor?: HiredEquipmentAnchor;
}

function draftIdFromSearch(search: string): string | null {
  const id = new URLSearchParams(search).get('draft')?.trim();
  return id || null;
}

const HiredEquipmentGridPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const stateAnchor = (location.state as GridLocationState | null)?.anchor;
  const draftIdFromUrl = draftIdFromSearch(location.search);
  const loadedDraftIdRef = useRef<string | null>(null);
  const [anchor, setAnchor] = useState<HiredEquipmentAnchor | null>(() =>
    draftIdFromUrl ? null : (stateAnchor ?? readHiredEquipmentAnchor()),
  );
  const [rows, setRows] = useState<HiredEquipmentGridRow[]>([]);
  const [prePopulateAttempted, setPrePopulateAttempted] = useState(() => Boolean(draftIdFromUrl));
  const [draftFormId, setDraftFormId] = useState<string | null>(draftIdFromUrl);
  const [hydratingDraft, setHydratingDraft] = useState(Boolean(draftIdFromUrl));
  const [draftLoadError, setDraftLoadError] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    referenceNumber: string;
    formId: string;
    attachmentCount: number;
    status: 'pending' | 'approved';
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [internalComment, setInternalComment] = useState('');
  const [taskModalRequest, setTaskModalRequest] = useState<HiredEquipmentTaskModalRequest | null>(null);
  /** Rows whose task clone is still in flight — their Duplicate and task picker are disabled. */
  // Duplicated lines no longer clone while editing (V2-011); nothing is ever in flight.
  const [cloningClientIds] = useState<Set<string>>(new Set());
  const [cloneError, setCloneError] = useState<string | null>(null);
  /**
   * H1: Operations Coordinator submit, waiting for the dialog's answer. Nothing has been saved yet –
   * the payload is held here and sent, with the answer, as one submit-and-approve call.
   */
  const [pendingSubmitApproved, setPendingSubmitApproved] = useState<HiredEquipmentSubmitPayload | null>(null);
  const [approving, setApproving] = useState(false);
  /** M13: discarding a form with lines asks first. */
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const { hasPermission } = usePermissions();
  const canCreateRoutineTask = hasPermission('routine:specific_create');
  const lookups = useHiredEquipmentGridData(anchor ?? { entryMethod: 'purchase_order' }, rows, {
    standingRoutineSelectable: canCreateRoutineTask,
  });
  const poBalanceWarning = usePoBalanceWarning();
  // C4 / M6: every line priced at the rate on its date; same-day hours checked across submissions.
  const pricing = useHeLinePricing(rows, setRows, {
    serviceAreaId: anchor?.serviceAreaId,
    draftId: draftFormId,
    enabled: anchor != null,
  });
  const sameDayBlocked = pricing.sameDayHours.some((entry) => entry.level === 'block');

  const availableTaskTypes = useMemo<TaskType[]>(() => {
    const types: TaskType[] = [];
    if (canCreateRoutineTask) types.push('Routine');
    if (hasPermission('quantified:task_create')) types.push('Quantified');
    return types;
  }, [canCreateRoutineTask, hasPermission]);

  /**
   * H1: Operations Coordinators submit with no further approval – the record is created Approved.
   * Everyone else (Supervisor, Admin) submits for approval by an Operations Coordinator.
   */
  const canAutoApprove = hasPermission(HE_AUTO_APPROVE_PERMISSION);

  useEffect(() => {
    if (stateAnchor) {
      persistHiredEquipmentAnchor(stateAnchor);
      setAnchor(stateAnchor);
    }
  }, [stateAnchor]);

  useEffect(() => {
    if (!anchor && !confirmation && !hydratingDraft && !draftLoadError) {
      navigate(SUBMISSIONS_DRAFTS_PATH, { replace: true });
    }
  }, [anchor, confirmation, navigate, hydratingDraft, draftLoadError]);

  useEffect(() => {
    if (!draftIdFromUrl || loadedDraftIdRef.current === draftIdFromUrl) return;
    let cancelled = false;
    setHydratingDraft(true);
    setDraftLoadError(null);
    getHiredEquipmentDraft(draftIdFromUrl)
      .then((draft) => {
        if (cancelled) return;
        loadedDraftIdRef.current = draft.formId;
        const nextAnchor = draft.anchor;
        setAnchor(nextAnchor);
        persistHiredEquipmentAnchor(nextAnchor);
        setRows(draft.rows);
        setInternalComment(draft.internalComment ?? '');
        setDraftFormId(draft.formId);
        setPrePopulateAttempted(true);
        setHydratingDraft(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setDraftLoadError(error instanceof Error ? error.message : 'Failed to open this draft');
        setHydratingDraft(false);
      });
    return () => {
      cancelled = true;
    };
  }, [draftIdFromUrl]);

  useEffect(() => {
    if (!anchor || prePopulateAttempted) return;
    setPrePopulateAttempted(true);

    const seed = async () => {
      if (anchor.prePopulate && anchor.lockedPoId) {
        try {
          const resources = await hiredEquipmentPoService.getPoResources(anchor.lockedPoId);
          if (resources.length > 0) {
            setRows(rowsFromPoResources(anchor, resources));
            return;
          }
        } catch (error) {
          console.error('Failed to pre-populate hired equipment grid from PO', error);
        }
      }
      setRows([]);
    };

    seed();
  }, [anchor, prePopulateAttempted]);

  useEffect(() => {
    if (lookups.vendors.length === 0) return;
    setRows((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (row.fuelSurchargeEligible != null) return row;
        if (row.vehicleType) {
          const fallback = defaultFuelSurchargeEligibleFromVehicleType(row.vehicleType);
          if (fallback == null) return row;
          changed = true;
          return { ...row, fuelSurchargeEligible: fallback };
        }
        if (row.vendorId == null || row.resourceId == null) return row;
        const resource = lookups.resourcesForRow(row).find((item) => item.id === row.resourceId);
        if (!resource?.vehicleType) return row;
        changed = true;
        return {
          ...row,
          vehicleType: resource.vehicleType,
          fuelSurchargeEligible: defaultFuelSurchargeEligibleFromVehicleType(resource.vehicleType),
        };
      });
      return changed ? next : prev;
    });
  }, [lookups.vendors, lookups.resourcesForRow]);

  if (hydratingDraft) {
    return <div className="tw-p-4 tw-text-sm tw-text-content-muted">Loading draft…</div>;
  }

  if (draftLoadError) {
    return (
      <div className="tw-p-4">
        <Alert variant="danger" className="tw-mb-3">
          {draftLoadError}
        </Alert>
        <button
          type="button"
          className="tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold tw-text-primary tw-underline"
          onClick={() => navigate(SUBMISSIONS_DRAFTS_PATH)}
        >
          Back to Drafts
        </button>
      </div>
    );
  }

  // V2-014: the submit result replaces the entry on this page – inline, not in a dialog.
  if (confirmation) {
    return (
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-p-4">
        <PageHeader title="New Hired Equipment" />
        <SubmissionConfirmationModal
          show
          referenceNumber={confirmation.referenceNumber}
          attachmentCount={confirmation.attachmentCount ?? 0}
          status={confirmation.status ?? 'pending'}
          onClose={() => navigate(SUBMISSIONS_DRAFTS_PATH, { replace: true })}
          onViewApprovals={() =>
            navigate(submissionRecordPath(confirmation.formId ?? '', confirmation.status ?? 'pending'))
          }
        />
      </div>
    );
  }

  if (!anchor) return null;

  const handleAddLine = () => {
    setRows((prev) => [...prev, emptyRowFromAnchor(anchor)]);
  };

  const handleAddLumpSumLine = () => {
    setRows((prev) => [...prev, lumpSumRowFromAnchor(anchor)]);
  };

  /**
   * Duplicate a line. When the original carries a specific routine task the copy needs its own:
   * a routine task here represents one piece of work, and two lines on one task bill it twice.
   *
   * V2-011: that task is created when the submission is submitted (`cloneTaskOnSubmit`), not now,
   * so deleting the copy or discarding the entry leaves no task behind. The server clones only
   * specific Routine tasks; any other task stays shared, as before.
   */
  const handleDuplicate = (clientId: string) => {
    const source = rows.find((row) => row.clientId === clientId);
    if (!source) return;
    // Cloning writes a real task, so it needs the same permission that gates creating one here.
    const canClone = source.taskId != null && availableTaskTypes.length > 0;
    const copy: HiredEquipmentGridRow = {
      ...duplicateRow(source),
      taskCreatedInSession: false,
      cloneTaskOnSubmit: canClone,
    };
    setRows((prev) => [...prev, copy]);
  };

  const handleRemove = (clientId: string) => {
    setRows((prev) => prev.filter((row) => row.clientId !== clientId));
  };

  const handleReAddPoResources = async () => {
    if (!anchor.lockedPoId) return;
    const resources = await hiredEquipmentPoService.getPoResources(anchor.lockedPoId);
    setRows((prev) => appendMissingPoResources(anchor, prev, resources));
  };

  const discardNow = async () => {
    if (draftFormId) {
      try {
        await discardHiredEquipmentDraft(draftFormId);
      } catch (error) {
        setSubmitError(error instanceof Error ? error.message : 'Failed to discard draft');
        return;
      }
    }
    clearHiredEquipmentAnchor();
    navigate(SUBMISSIONS_DRAFTS_PATH);
  };

  const handleDiscard = () => {
    // M13: a form with lines, or a saved draft, asks first.
    if (rows.length > 0 || draftFormId) {
      setConfirmDiscard(true);
      return;
    }
    void discardNow();
  };

  const handleSaveDraft = async () => {
    if (savingDraft || submitting) return;
    setSavingDraft(true);
    setSubmitError(null);
    try {
      await saveHiredEquipmentDraft({ anchor, rows, internalComment }, draftFormId);
      clearHiredEquipmentAnchor();
      navigate(SUBMISSIONS_DRAFTS_PATH, { replace: true });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleTaskCreated = (rowClientId: string, task: TaskOption) => {
    lookups.registerTask(task);
    setRows((prev) =>
      prev.map((row) =>
        row.clientId === rowClientId
          ? {
              ...row,
              // keepPo: a task created just now cannot be on any PO's line items, which are server
              // data that predate it, so revalidating the PO against it would always release it.
              ...nextRowAfterTaskChange(row, task, lookups.tasksByPoId, formatTaskLabel, true),
              // The submission owns a task it created, so approval may close it.
              taskCreatedInSession: true,
              // This path bypasses the grid's own `patch`, which is what normally clears the
              // duplicate shading — picking a task is an edit, so clear it here.
              duplicatedPending: false,
            }
          : row,
      ),
    );
  };

  const handleTaskUpdated = (task: TaskOption) => {
    lookups.registerTask(task);
    const label = formatTaskLabel(task);
    setRows((prev) =>
      prev.map((row) => (row.taskId === task.id && row.taskLabel !== label ? { ...row, taskLabel: label } : row)),
    );
  };

  const validation = validateGridRows(rows);
  const showValidation = attemptedSubmit && !validation.ok;

  /** Record the submission for this session and show the confirmation screen. */
  const finishSubmission = (
    submitted: { formId: string; referenceNumber: string; status?: 'pending' | 'approved' },
    fallbackStatus: 'pending' | 'approved',
  ) => {
    const status = submitted.status ?? fallbackStatus;
    // H14: the Forms list is loaded from the server, so nothing about the submission is kept in
    // browser storage; only the in-progress entry is cleared.
    clearHiredEquipmentAnchor();
    setConfirmation({
      referenceNumber: submitted.referenceNumber,
      formId: submitted.formId,
      attachmentCount: rows.filter((row) => row.attachment).length,
      status,
    });
  };

  const buildSubmitPayload = (): HiredEquipmentSubmitPayload => ({
    anchor: {
      entryMethod: anchor.entryMethod,
      lockedVendorName: anchor.lockedVendorName,
      lockedPoNumber: anchor.lockedPoNumber,
      serviceAreaId: anchor.serviceAreaId,
    },
    rows: heGridSubmitRows(rows),
    internalComment,
    draftId: draftFormId,
  });

  const persistAndSubmit = async () => {
    const payload = buildSubmitPayload();
    // H1: an Operations Coordinator answers the dialog first; nothing is saved until they do, and
    // closing it saves nothing.
    if (canAutoApprove) {
      setPendingSubmitApproved(payload);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const submitted = await submitHiredEquipment(payload);
      finishSubmission(submitted, 'pending');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Could not submit hired equipment.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * H1: the Operations Coordinator's answer. Create and approve happen in one server call; if the
   * approval fails the server removes the record, so the dialog stays open with the error and the
   * grid keeps every line.
   */
  const completeSubmitApproved = async (emailVendorPdf: boolean, vendorEmail?: HeVendorEmailDraft) => {
    if (!pendingSubmitApproved || approving) return;
    setApproving(true);
    try {
      const submitted = await submitHiredEquipment({
        ...pendingSubmitApproved,
        submitAndApprove: true,
        emailVendorPdf,
        ...(vendorEmail ? { vendorEmail } : {}),
      });
      setPendingSubmitApproved(null);
      finishSubmission(submitted, 'approved');
    } finally {
      setApproving(false);
    }
  };

  const handleSubmit = async () => {
    setAttemptedSubmit(true);
    setSubmitError(null);
    const result = validateGridRows(rows);
    if (!result.ok) {
      const firstLine = result.invalidLineNumbers[0];
      if (firstLine != null) scrollToGridLine(firstLine);
      // V2-018: focus moves to the first field with a problem; its message is under it.
      setTimeout(() => {
        const target = document.querySelector<HTMLElement>(
          '[data-invalid="true"] input:not([disabled]), [data-invalid="true"] select:not([disabled]), [data-invalid="true"] button:not([disabled]), [data-invalid="true"] textarea:not([disabled])',
        );
        target?.focus();
      }, 0);
      return;
    }
    if (sameDayBlocked) {
      setSubmitError('A resource has more than 24 hours on one date. Fix the hours shown above the grid before submitting.');
      return;
    }
    const unpriced = rows.findIndex((row) => !row.lumpSum && row.noRateOnDate);
    if (unpriced >= 0) {
      scrollToGridLine(unpriced + 1);
      setSubmitError(`Line ${unpriced + 1} has no approved rate on its date. Change the date or resource.`);
      return;
    }
    const okToSubmit = await poBalanceWarning.check({
      rows,
      lockedPoId: anchor.lockedPoId,
      purchaseOrders: lookups.purchaseOrders,
      fuelSurcharges: lookups.fuelSurcharges,
    });
    if (!okToSubmit) return;
    await persistAndSubmit();
  };

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-p-4">
      <PageHeader
        title={hiredEquipmentGridPageTitle(anchor)}
        subtitle={
          draftFormId
            ? `${methodLabel(anchor.entryMethod)} · Draft (not in approvals until you submit)`
            : `Entry method: ${methodLabel(anchor.entryMethod)}`
        }
        actions={[
          {
            key: 'discard',
            label: 'Discard',
            icon: 'bi-x-lg',
            intent: 'neutral',
            variant: 'light',
            alwaysShowLabel: true,
            disabled: savingDraft || submitting,
            onClick: () => void handleDiscard(),
          },
          {
            key: 'save-draft',
            label: savingDraft ? 'Saving…' : 'Save as Draft',
            icon: 'bi-save',
            intent: 'secondary',
            alwaysShowLabel: true,
            disabled: savingDraft || submitting || poBalanceWarning.checking,
            onClick: () => void handleSaveDraft(),
          },
          {
            key: 'submit',
            label: submitting
              ? 'Submitting…'
              : poBalanceWarning.checking
                ? 'Checking…'
                : canAutoApprove
                  ? 'Submit'
                  : 'Submit for approval',
            icon: 'bi-check-lg',
            intent: 'primary',
            alwaysShowLabel: true,
            disabled: submitting || savingDraft || poBalanceWarning.checking,
            onClick: handleSubmit,
          },
        ]}
      />

      <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4 tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-px-4 tw-py-3 tw-shadow-sm">
        <div className="tw-min-w-0">
          <p className="tw-mb-0.5 tw-font-display tw-text-xs tw-font-semibold tw-tracking-wide tw-text-content-muted">
            Entry Basis
          </p>
          <p className="tw-mb-0 tw-text-base tw-font-semibold tw-text-content">{formatAnchorValue(anchor)}</p>
        </div>
        <p className="tw-mb-0 tw-text-sm tw-text-content-muted">
          <span className="tw-mr-1 tw-inline-block tw-h-2 tw-w-2 tw-rounded-full tw-bg-success" aria-hidden="true" />
          Mandatory fields marked * – enforced before submit
        </p>
      </div>

      {/* V2-018 / FORM-01: each problem shows under its own field; only an empty grid needs a
          message of its own. */}
      {showValidation && rows.length === 0 && (
        <Alert variant="danger" className="tw-mb-3">
          Add at least one line before submitting.
        </Alert>
      )}

      {submitError && (
        <Alert variant="danger" className="tw-mb-3">
          {submitError}
        </Alert>
      )}

      {pricing.pricingError && (
        <Alert variant="warning" className="tw-mb-3">
          {pricing.pricingError}
        </Alert>
      )}

      {cloneError && (
        <Alert variant="warning" className="tw-mb-3" onClose={() => setCloneError(null)} dismissible>
          {cloneError}
        </Alert>
      )}

      <HiredEquipmentGrid
        anchor={anchor}
        rows={rows}
        onRowsChange={setRows}
        vendors={lookups.vendors}
        taskOptionForRow={lookups.taskOptionForRow}
        searchTasks={lookups.searchTasks}
        purchaseOrders={lookups.purchaseOrders}
        tasksByPoId={lookups.tasksByPoId}
        resourcesForRow={lookups.resourcesForRow}
        fuelSurcharges={lookups.fuelSurcharges}
        attemptedSubmit={attemptedSubmit}
        onAddLine={handleAddLine}
        onAddLumpSumLine={handleAddLumpSumLine}
        onDuplicateLine={handleDuplicate}
        onRemoveLine={handleRemove}
        onReAddPoResources={anchor.entryMethod === 'purchase_order' ? handleReAddPoResources : undefined}
        onCreateTask={
          availableTaskTypes.length > 0
            ? (row) => setTaskModalRequest({ kind: 'create', rowClientId: row.clientId })
            : undefined
        }
        onViewTask={(task) => setTaskModalRequest({ kind: 'view', taskId: task.id })}
        onStandingTaskPicked={
          canCreateRoutineTask
            ? (row, task) => setTaskModalRequest({ kind: 'create', rowClientId: row.clientId, baseTask: task })
            : undefined
        }
        cloningClientIds={cloningClientIds}
        sameDayHours={pricing.sameDayHours}
        canComment
      />

      <p className="tw-mb-0 tw-mt-3 tw-text-sm tw-text-content-muted">
        Amount = qty × rate, plus fuel $/hr on regular hours when eligible. Overtime hours (after 8) bill at
        1.5× / 2× the rate without fuel. Each line is priced at the rate in effect on its date. Markup is
        applied later from the task costing, not on this form. Fuel is looked up from the fuels table by
        On-Road / Off-Road / N/A and the line date. A lump sum row bills the amount you type, once – no
        resource, qty, rate, fuel, or overtime.
      </p>

      <HiredEquipmentInternalComment
        value={internalComment}
        onChange={setInternalComment}
        disabled={submitting || savingDraft}
        className="tw-mt-4"
      />

      <HiredEquipmentTaskModals
        request={taskModalRequest}
        onClose={() => setTaskModalRequest(null)}
        onTaskCreated={handleTaskCreated}
        onTaskUpdated={handleTaskUpdated}
        availableTypes={availableTaskTypes}
      />

      <PoBalanceWarningModal
        show={poBalanceWarning.warnings != null}
        warnings={poBalanceWarning.warnings ?? []}
        onCancel={poBalanceWarning.dismiss}
        onConfirm={() => {
          poBalanceWarning.dismiss();
          void persistAndSubmit();
        }}
      />

      {/*
        H1 – Operations Coordinator submit. Shown before anything is saved; the answer and the
        submission go to the server together as one submit-and-approve. Cancel saves nothing.
      */}
      <HeVendorApprovalEmailModal
        isOpen={pendingSubmitApproved != null}
        formId={null}
        submitPayload={pendingSubmitApproved}
        busy={approving}
        onCancel={() => {
          if (approving) return;
          setPendingSubmitApproved(null);
        }}
        onSkip={() => completeSubmitApproved(false)}
        onSend={(draft) => completeSubmitApproved(true, draft)}
      />

      <HeConfirmModal
        show={confirmDiscard}
        title={draftFormId ? 'Discard this draft?' : 'Discard this form?'}
        confirmLabel="Discard"
        confirmVariant="danger"
        cancelLabel="Keep editing"
        onCancel={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          void discardNow();
        }}
      >
        <p className="tw-mb-0 tw-text-sm tw-text-content">
          {draftFormId
            ? 'The saved draft, with its lines and photos, will be deleted.'
            : `The ${rows.length === 1 ? 'line' : `${rows.length} lines`} you entered will be lost.`}{' '}
          This cannot be undone.
        </p>
      </HeConfirmModal>

    </div>
  );
};

export default HiredEquipmentGridPage;
